import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Globe, MapPin, Smartphone, ChevronDown, ChevronUp } from 'lucide-react';
import { mockData } from '../../mock/data';
import { useTranslation } from '../../hooks/useTranslation';

const LocationsSection = ({ showTitle = true, variant = 'full' }) => {
  const [showAllCountries, setShowAllCountries] = useState(false);
  const { locations } = mockData;
  const { t, language } = useTranslation();
  
  const getTranslatedCountryName = (countryName) => {
    if (language === 'zh') {
      return t(`locations.countries.${countryName}`) || countryName;
    }
    return countryName;
  };
  
  const displayedCountries = showAllCountries 
    ? locations.countries 
    : locations.countries.slice(0, 12);

  if (variant === 'compact') {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center mb-4">
          <Globe className="w-6 h-6 text-cyan-400 mr-3" />
          <div>
            <h3 className="text-lg font-semibold text-white">{t('locations.globalCoverage')}</h3>
            <p className="text-slate-400 text-sm">{locations.totalCountries} countries • {locations.totalCities}+ cities</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {locations.countries.slice(0, 8).map((country, index) => (
            <Badge key={index} variant="outline" className="border-slate-700 text-slate-300 text-xs">
              {getTranslatedCountryName(country)}
            </Badge>
          ))}
          <Badge variant="outline" className="border-cyan-500/50 text-cyan-400 text-xs">
            +{locations.totalCountries - 8} {language === 'zh' ? '更多' : 'more'}
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-slate-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {showTitle && (
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <MapPin className="w-8 h-8 text-cyan-400 mr-3" />
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                {language === 'zh' ? (
                  <span className="text-cyan-400">{t('locations.title')}</span>
                ) : (
                  <>Available <span className="text-cyan-400">Locations</span></>
                )}
              </h2>
            </div>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              {language === 'zh' ? t('locations.subtitle') : 
                `Access mobile and residential proxies from ${locations.totalCountries} countries and ${locations.totalCities}+ cities worldwide with premium carrier networks.`
              }
            </p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div className="text-center bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
            <div className="w-16 h-16 bg-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{locations.totalCountries}+</div>
            <div className="text-slate-400">{t('locations.countriesAvailable')}</div>
          </div>
          
          <div className="text-center bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{locations.totalCities}+</div>
            <div className="text-slate-400">{t('locations.citiesCovered')}</div>
          </div>
          
          <div className="text-center bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{locations.mobileCarriers.length}+</div>
            <div className="text-slate-400">{t('locations.mobileCarriers')}</div>
          </div>
        </div>

        {/* Countries Grid */}
        <div className="bg-slate-900/30 border border-slate-800 rounded-3xl p-8">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">{t('locations.supportedCountries')}</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
            {displayedCountries.map((country, index) => (
              <div 
                key={index}
                className="flex items-center p-3 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-cyan-500/50 hover:bg-slate-800/70 transition-all duration-200"
              >
                <div className="w-3 h-3 bg-green-400 rounded-full mr-3 flex-shrink-0"></div>
                <span className="text-slate-300 text-sm font-medium">{getTranslatedCountryName(country)}</span>
              </div>
            ))}
          </div>
          
          {locations.countries.length > 12 && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => setShowAllCountries(!showAllCountries)}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                {showAllCountries ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    {t('locations.showLessCountries')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    {language === 'zh' ? `${t('locations.showAllCountries')} ${locations.totalCountries} 个` : `Show All ${locations.totalCountries} Countries`}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Top Mobile Carriers */}
        <div className="mt-12 bg-slate-900/30 border border-slate-800 rounded-3xl p-8">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">{t('locations.premiumMobileCarriers')}</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {locations.mobileCarriers.slice(0, 10).map((carrier, index) => (
              <div 
                key={index}
                className="text-center p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-cyan-500/50 hover:bg-slate-800/70 transition-all duration-200"
              >
                <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Smartphone className="w-4 h-4 text-white" />
                </div>
                <span className="text-slate-300 text-sm font-medium">{carrier}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-6">
            <p className="text-slate-400 text-sm">
              {t('locations.moreCarriersText')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;